import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '../components/ui/dialog';
import { Loader2, ArrowLeft, Search, ChevronLeft, ChevronRight, Filter, List } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { VisiteDetailContent } from '../components/visites/VisiteDetailDialog';
import { VisiteFormContent } from '../components/visites/VisiteFormDialog';
import { useIsPhone } from '../hooks/useIsPhone';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const JOURS_SEMAINE = ['DIM.', 'LUN.', 'MAR.', 'MER.', 'JEU.', 'VEN.', 'SAM.'];
const JOURS_COMPLETS = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
const MOIS_NOMS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

const UNASSIGNED_VIRTUELLE_COLOR = '#a3e635';
const UNASSIGNED_PRESENTIELLE_COLOR = '#f87171';
const ANNULEE_COLOR = '#991b1b';
const DECALEE_COLOR = '#f59e0b';

function getVisiteColor(v) {
    if (v.statut === 'annulee') return ANNULEE_COLOR;
    if (v.statut === 'decalee') return DECALEE_COLOR;
    if (v.commercial_id && v.commercial_couleur) return v.commercial_couleur;
    if (!v.commercial_id) {
        return v.type_visite === 'virtuelle' ? UNASSIGNED_VIRTUELLE_COLOR : UNASSIGNED_PRESENTIELLE_COLOR;
    }
    return v.commercial_couleur || '#94a3b8';
}

function MobileListView({ visites, onVisiteClick, searchQuery, filterBy, isPhone, continuous }) {
    const [currentMonth, setCurrentMonth] = useState(() => {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth(), 1);
    });
    // Mode continu : borne basse d'affichage (défaut : 1er jour du mois précédent)
    const [minDate, setMinDate] = useState(() => {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().split('T')[0];
    });

    const searchActive = !!searchQuery.trim();
    // Recherche = tout l'agenda ; téléphone = liste continue sans changement de mois
    const showAll = continuous || searchActive;

    const filtered = visites.filter(v => {
        if (filterBy && filterBy !== 'all') {
            if (filterBy === 'annulee' && v.statut !== 'annulee') return false;
            if (filterBy === 'decalee' && v.statut !== 'decalee') return false;
            if (filterBy === 'non_assignee' && v.commercial_id) return false;
            if (filterBy === 'virtuelle_non_assignee' && !(v.type_visite === 'virtuelle' && !v.commercial_id)) return false;
            if (filterBy === 'presentielle_non_assignee' && !(v.type_visite === 'presentielle' && !v.commercial_id)) return false;
            if (filterBy.startsWith('comm_')) {
                const commId = filterBy.replace('comm_', '');
                if (v.commercial_id !== commId) return false;
            }
        }
        if (!searchActive) return true;
        return (v.prospect_nom || '').toLowerCase().includes(searchQuery.toLowerCase());
    });

    const groupedByDate = useMemo(() => {
        const groups = {};
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth() + 1;
        const monthStr = `${year}-${String(month).padStart(2, '0')}`;
        filtered.forEach(v => {
            if (!v.date) return;
            if (showAll) {
                if (continuous && !searchActive && v.date < minDate) return;
            } else if (!v.date.startsWith(monthStr)) {
                return;
            }
            if (!groups[v.date]) groups[v.date] = [];
            groups[v.date].push(v);
        });
        Object.keys(groups).forEach(date => {
            groups[date].sort((a, b) => (a.heure || '').localeCompare(b.heure || ''));
        });
        return groups;
    }, [filtered, currentMonth, showAll, continuous, searchActive, minDate]);

    const sortedDates = Object.keys(groupedByDate).sort();
    const totalResults = sortedDates.reduce((s, d) => s + groupedByDate[d].length, 0);

    const prevMonth = () => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
    const nextMonth = () => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));

    const monthLabel = `${MOIS_NOMS[currentMonth.getMonth()]} ${currentMonth.getFullYear()}`;
    const todayStr = new Date().toISOString().split('T')[0];

    // Scroll automatique vers la date du jour (ou la prochaine si pas d'events aujourd'hui) — style Google Agenda
    const todayRef = useRef(null);
    const nextDayRef = useRef(null);
    const hasAutoScrolledRef = useRef(false);

    const goToday = () => {
        if (continuous) {
            const target = todayRef.current || nextDayRef.current;
            if (target) { try { target.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch {} }
            return;
        }
        const now = new Date();
        setCurrentMonth(new Date(now.getFullYear(), now.getMonth(), 1));
    };

    useEffect(() => {
        if (searchActive) return;
        const now = new Date();
        const isCurrentMonth = continuous || (currentMonth.getFullYear() === now.getFullYear() && currentMonth.getMonth() === now.getMonth());
        if (isCurrentMonth && !hasAutoScrolledRef.current && sortedDates.length > 0) {
            const target = todayRef.current || nextDayRef.current;
            if (target) {
                hasAutoScrolledRef.current = true;
                requestAnimationFrame(() => {
                    try { target.scrollIntoView({ behavior: 'auto', block: 'start' }); } catch {}
                });
            }
        }
        if (!isCurrentMonth) hasAutoScrolledRef.current = false;
    }, [currentMonth, sortedDates.length, continuous, searchActive]);

    // Trouver le 1er jour >= aujourd'hui parmi sortedDates pour le scroll de secours
    const upcomingDateStr = sortedDates.find(d => d >= todayStr);

    return (
        <div data-testid="mobile-list-view">
            {searchActive ? (
                <div className="mb-4 px-1">
                    <h2 className="text-base font-semibold text-slate-800" data-testid="mobile-search-results-count">
                        {totalResults} résultat{totalResults > 1 ? 's' : ''} dans tout l'agenda
                    </h2>
                </div>
            ) : continuous ? (
                <div className="flex items-center justify-between mb-3 px-1">
                    <button onClick={goToday} className="px-3 py-1 text-sm font-medium rounded-md bg-[#2e7cd6] text-white" data-testid="mobile-today-btn">Aujourd'hui</button>
                    <button
                        onClick={() => setMinDate(prev => {
                            const d = new Date(prev + 'T00:00:00');
                            d.setMonth(d.getMonth() - 2);
                            return d.toISOString().split('T')[0];
                        })}
                        className="text-xs font-medium text-[#2e7cd6] px-3 py-1.5 rounded-md border border-blue-200 bg-blue-50"
                        data-testid="mobile-load-older"
                    >
                        Mois précédents
                    </button>
                </div>
            ) : (
                <div className="flex items-center justify-between mb-4 px-1">
                    <div className="flex items-center gap-2">
                        <button onClick={prevMonth} className="p-1.5 rounded-md hover:bg-slate-100" data-testid="mobile-prev-month"><ChevronLeft size={20} className="text-slate-600" /></button>
                        <button onClick={nextMonth} className="p-1.5 rounded-md hover:bg-slate-100" data-testid="mobile-next-month"><ChevronRight size={20} className="text-slate-600" /></button>
                        <button onClick={goToday} className="px-3 py-1 text-sm font-medium rounded-md bg-[#2e7cd6] text-white" data-testid="mobile-today-btn">Aujourd'hui</button>
                    </div>
                    <h2 className="text-lg font-semibold text-slate-800 capitalize">{monthLabel}</h2>
                </div>
            )}

            {sortedDates.length === 0 ? (
                <div className="text-center py-12 text-slate-400">{searchActive ? 'Aucun résultat dans tout l\'agenda' : showAll ? 'Aucune visite' : 'Aucune visite ce mois'}</div>
            ) : (
                <div className="divide-y divide-slate-200">
                    {sortedDates.map((dateStr, idx) => {
                        const d = new Date(dateStr + 'T00:00:00');
                        const dayName = JOURS_SEMAINE[d.getDay()];
                        const dayNum = d.getDate();
                        const list = groupedByDate[dateStr];
                        const isToday = dateStr === todayStr;
                        const isUpcoming = !isToday && dateStr === upcomingDateStr;
                        const monthKey = dateStr.slice(0, 7);
                        const showMonthHeader = showAll && (idx === 0 || sortedDates[idx - 1].slice(0, 7) !== monthKey);

                        return (
                            <div key={dateStr}>
                                {showMonthHeader && (
                                    <div className="sticky top-0 z-10 px-3 py-1.5 bg-blue-50 border-y border-blue-100 text-sm font-bold text-slate-700 capitalize" data-testid={`month-header-${monthKey}`}>
                                        {MOIS_NOMS[d.getMonth()]} {d.getFullYear()}
                                    </div>
                                )}
                                <div ref={isToday ? todayRef : (isUpcoming ? nextDayRef : undefined)} className={`flex gap-3 ${isPhone ? 'py-2' : 'py-3'} px-1`} data-testid={`mobile-date-group-${dateStr}`}>
                                <div className={`${isPhone ? 'w-10' : 'w-14'} shrink-0 text-center pt-1`}>
                                    <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">{dayName}</p>
                                    <p className={`text-2xl font-bold leading-tight ${isToday ? 'text-[#2e7cd6]' : 'text-slate-800'}`}>{dayNum}</p>
                                </div>
                                <div className="flex-1 space-y-1.5 min-w-0">
                                    {list.map(v => {
                                        const color = getVisiteColor(v);
                                        const typeLabel = v.type_visite === 'presentielle' ? 'Présentielle' : 'Virtuelle';
                                        return (
                                            <button
                                                key={v.id}
                                                type="button"
                                                onClick={() => onVisiteClick(v)}
                                                className={`w-full text-left rounded-md px-3 ${isPhone ? 'py-1.5' : 'py-2'} cursor-pointer hover:brightness-110 transition`}
                                                style={{ backgroundColor: color }}
                                                data-testid={`mobile-visite-${v.id}`}
                                            >
                                                {isPhone ? (
                                                    <>
                                                        <p className="text-white text-sm font-medium flex items-baseline gap-2 min-w-0">
                                                            <span className="tabular-nums shrink-0">{v.heure}</span>
                                                            <span className="truncate">{v.prospect_nom || 'Client'}</span>
                                                        </p>
                                                        <p className="text-white/80 text-[11px] leading-tight">{typeLabel}</p>
                                                    </>
                                                ) : (
                                                    <>
                                                        <p className="text-white text-sm font-medium truncate">{v.prospect_nom || 'Client'}</p>
                                                        <p className="text-white/80 text-xs">{v.heure} - {typeLabel}</p>
                                                    </>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export function CalendrierPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const isPhone = useIsPhone();
    const hideListButton = isPhone && user?.role !== 'admin';
    const [visites, setVisites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterBy, setFilterBy] = useState('all');
    const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

    const [selectedVisite, setSelectedVisite] = useState(null);
    const [dialogMode, setDialogMode] = useState(null);
    const [prospectConfig, setProspectConfig] = useState({});
    const [currentTitle, setCurrentTitle] = useState('');
    const [commerciaux, setCommerciaux] = useState([]);
    const [tomorrowUnassigned, setTomorrowUnassigned] = useState({ count: 0, date: '' });
    const [showTomorrowAlert, setShowTomorrowAlert] = useState(false);
    const calendarRef = useRef(null);

    useEffect(() => {
        axios.get(`${API}/api/prospects/config`, { withCredentials: true })
            .then(({ data }) => setProspectConfig(data)).catch(() => {});
        axios.get(`${API}/api/admin/users`, { withCredentials: true })
            .then(({ data }) => setCommerciaux(data.filter(u => u.couleur && u.role === 'commercial'))).catch(() => {});
    }, []);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 1024);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const fetchTomorrowUnassigned = useCallback(() => {
        axios.get(`${API}/api/visites/tomorrow-unassigned`, { withCredentials: true })
            .then(({ data }) => setTomorrowUnassigned({ count: data.count || 0, date: data.date || '' }))
            .catch(() => {});
    }, []);

    // Fetch unassigned visites for tomorrow; alert visible from 14h onwards of current day
    useEffect(() => {
        const checkAlertVisibility = () => {
            const h = new Date().getHours();
            setShowTomorrowAlert(h >= 14);
        };
        checkAlertVisibility();
        const intervalVisibility = setInterval(checkAlertVisibility, 60000); // re-check every minute

        fetchTomorrowUnassigned();
        const intervalFetch = setInterval(fetchTomorrowUnassigned, 5 * 60 * 1000); // every 5 min

        return () => {
            clearInterval(intervalVisibility);
            clearInterval(intervalFetch);
        };
    }, [fetchTomorrowUnassigned]);

    const fetchVisites = useCallback(async () => {
        try {
            // Cache-buster `?_t=` (Safari/iPad cache les GET trop agressivement)
            const { data } = await axios.get(`${API}/api/visites?_t=${Date.now()}`, {
                withCredentials: true
            });
            setVisites(data);
            // Quand on recharge la liste (après création/maj/suppression), on rafraîchit
            // aussi le compteur "non affectées demain" pour que le badge se mette à jour
            // immédiatement sans avoir à recharger la page.
            fetchTomorrowUnassigned();
        } catch (error) {
            toast.error('Erreur lors du chargement des visites');
        } finally {
            setLoading(false);
        }
    }, [fetchTomorrowUnassigned]);

    useEffect(() => {
        setLoading(true);
        fetchVisites();
    }, [fetchVisites]);

    const [showReprogrammerModal, setShowReprogrammerModal] = useState(false);
    const [reprogrammerVisite, setReprogrammerVisite] = useState(null);
    const [reprogrammerRemarque, setReprogrammerRemarque] = useState('');
    const [reprogrammerSaving, setReprogrammerSaving] = useState(false);

    const filteredVisites = visites.filter(v => {
        // Filter by type
        if (filterBy !== 'all') {
            if (filterBy === 'annulee' && v.statut !== 'annulee') return false;
            if (filterBy === 'decalee' && v.statut !== 'decalee') return false;
            if (filterBy === 'non_assignee' && v.commercial_id) return false;
            if (filterBy === 'virtuelle_non_assignee' && !(v.type_visite === 'virtuelle' && !v.commercial_id)) return false;
            if (filterBy === 'presentielle_non_assignee' && !(v.type_visite === 'presentielle' && !v.commercial_id)) return false;
            // Filter by commercial id
            if (filterBy.startsWith('comm_')) {
                const commId = filterBy.replace('comm_', '');
                if (v.commercial_id !== commId) return false;
            }
        }
        if (!searchQuery.trim()) return true;
        return (v.prospect_nom || '').toLowerCase().includes(searchQuery.toLowerCase());
    });

    const searchActive = !!searchQuery.trim();

    const calendarEvents = filteredVisites.map(v => {
        const dateTime = `${v.date}T${v.heure}`;
        const bgColor = getVisiteColor(v);
        return {
            id: v.id,
            title: `${v.prospect_nom || 'Client'}`,
            start: dateTime,
            backgroundColor: bgColor,
            borderColor: bgColor,
            textColor: 'white',
            extendedProps: { visite: v }
        };
    });

    const renderEventContent = (eventInfo) => {
        const time = eventInfo.timeText;
        const title = eventInfo.event.title;
        return (
            <div className="flex items-center gap-0 px-1 py-0.5 text-xs font-normal overflow-hidden whitespace-nowrap" data-testid="calendar-event-content">
                <span>{time}</span>
                <span className="mx-1 opacity-60">|</span>
                <span className="truncate">{title}</span>
            </div>
        );
    };

    const handleEventClick = (info) => {
        const visite = info.event.extendedProps.visite;
        setSelectedVisite(visite);
        setDialogMode('detail');
    };

    const handleMobileVisiteClick = (visite) => {
        setSelectedVisite(visite);
        setDialogMode('detail');
    };

    const handleCloseDialog = () => {
        setDialogMode(null);
        setSelectedVisite(null);
    };

    const handleEditSuccess = async () => {
        await fetchVisites();
        if (selectedVisite) {
            try {
                // Cache-buster `?_t=` pour eviter que Safari/iPad serve une version
                // cachee de la visite (bug "le statut revient a l'ancienne valeur").
                const { data } = await axios.get(`${API}/api/visites/${selectedVisite.id}?_t=${Date.now()}`, { withCredentials: true });
                setSelectedVisite(data);
            } catch {}
        }
        setDialogMode('detail');
    };

    const handleDetailRefresh = async () => {
        await fetchVisites();
        if (selectedVisite) {
            try {
                const { data } = await axios.get(`${API}/api/visites/${selectedVisite.id}?_t=${Date.now()}`, { withCredentials: true });
                setSelectedVisite(data);
            } catch {}
        }
    };

    return (
        <div className="flex flex-col h-[calc(100vh-64px)]" data-testid="calendrier-page">
            {/* Header - white background like Planifier */}
            <div className="shrink-0 p-3 md:p-4 border-b bg-white">
                <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate('/')}
                            data-testid="calendrier-back-btn"
                        >
                            <ArrowLeft size={16} className="mr-1" />
                            <span className="hidden sm:inline">Retour</span>
                        </Button>
                        <h1
                            className="text-lg md:text-xl font-bold text-slate-900 tracking-tight"
                            style={{ fontFamily: 'Manrope, sans-serif' }}
                            data-testid="calendrier-title"
                        >
                            Calendrier des visites
                        </h1>
                    </div>
                    <div className="flex items-center gap-2 mr-12 lg:mr-16">
                        {/* Alerte visites non affectées pour demain — visible à partir de 14h, pour tous les rôles */}
                        {showTomorrowAlert && tomorrowUnassigned.count > 0 && (
                            <button
                                type="button"
                                onClick={() => {
                                    setFilterBy('non_assignee');
                                    if (calendarRef.current && tomorrowUnassigned.date) {
                                        try { calendarRef.current.getApi().gotoDate(tomorrowUnassigned.date); } catch {}
                                    }
                                }}
                                className="flex items-center gap-2 px-2.5 py-1.5 rounded-md border border-red-300 bg-white hover:bg-red-50 text-red-800 text-xs shadow-sm blink-urgent"
                                title="Cliquez pour filtrer et voir les visites non affectées de demain"
                                data-testid="tomorrow-unassigned-alert"
                            >
                                <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none">!</span>
                                <span className="font-semibold">
                                    {tomorrowUnassigned.count}
                                </span>
                                <span className="text-[11px] text-red-700">non affectée{tomorrowUnassigned.count > 1 ? 's' : ''} demain</span>
                            </button>
                        )}
                        {!hideListButton && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate('/visites-liste')}
                            className="border-[#2e7cd6] text-[#2e7cd6] hover:bg-[#2e7cd6] hover:text-white"
                            data-testid="calendrier-afficher-liste-btn"
                        >
                            <List size={16} className="mr-1.5" />
                            Afficher la liste
                        </Button>
                        )}
                    </div>
                </div>

                {/* Search bar + Filter */}
                <div className="flex items-center gap-3">
                    <div className="relative flex-1 max-w-sm">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input
                            placeholder="Rechercher un client..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-10"
                            data-testid="calendrier-search-input"
                        />
                    </div>
                    <Select value={filterBy} onValueChange={setFilterBy}>
                        <SelectTrigger className="w-[220px]" data-testid="calendrier-filter">
                            <Filter size={14} className="mr-2 text-slate-400" />
                            <SelectValue placeholder="Tous" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Tous</SelectItem>
                            {commerciaux.map(c => (
                                <SelectItem key={c.id} value={`comm_${c.id}`}>
                                    <div className="flex items-center gap-2">
                                        <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: c.couleur }} />
                                        {c.name}
                                    </div>
                                </SelectItem>
                            ))}
                            <SelectItem value="non_assignee">
                                <div className="flex items-center gap-2">
                                    <span className="w-2.5 h-2.5 rounded-sm shrink-0 bg-red-500" />
                                    Toutes non assignées
                                </div>
                            </SelectItem>
                            <SelectItem value="virtuelle_non_assignee">
                                <div className="flex items-center gap-2">
                                    <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: UNASSIGNED_VIRTUELLE_COLOR }} />
                                    Virtuelle non assignee
                                </div>
                            </SelectItem>
                            <SelectItem value="presentielle_non_assignee">
                                <div className="flex items-center gap-2">
                                    <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: UNASSIGNED_PRESENTIELLE_COLOR }} />
                                    Presentielle non assignee
                                </div>
                            </SelectItem>
                            <SelectItem value="annulee">
                                <div className="flex items-center gap-2">
                                    <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: ANNULEE_COLOR }} />
                                    Annulée
                                </div>
                            </SelectItem>
                            <SelectItem value="decalee">
                                <div className="flex items-center gap-2">
                                    <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: DECALEE_COLOR }} />
                                    À reprogrammer
                                </div>
                            </SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Date navigation bar - desktop only, masquée pendant une recherche (résultats sur tout l'agenda) */}
            {!searchActive && (
            <div className="shrink-0 hidden lg:flex items-center gap-3 px-4 py-2.5 bg-blue-50 border-b border-blue-200">
                <button onClick={() => {
                    if (calendarRef.current) calendarRef.current.getApi().prev();
                }} className="p-2.5 rounded-full bg-white border border-slate-300 hover:bg-slate-100 hover:border-slate-400 transition-all shadow-sm" data-testid="calendrier-prev">
                    <ChevronLeft size={22} className="text-slate-700" />
                </button>
                <button onClick={() => {
                    if (calendarRef.current) calendarRef.current.getApi().next();
                }} className="p-2.5 rounded-full bg-white border border-slate-300 hover:bg-slate-100 hover:border-slate-400 transition-all shadow-sm" data-testid="calendrier-next">
                    <ChevronRight size={22} className="text-slate-700" />
                </button>
                <button onClick={() => {
                    if (calendarRef.current) calendarRef.current.getApi().today();
                }} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-[#2e7cd6] text-white hover:bg-[#2568b8] transition-colors" data-testid="calendrier-today-btn">
                    Aujourd'hui
                </button>
                <p className="text-base font-bold text-slate-800 capitalize" data-testid="calendrier-current-label" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {currentTitle}
                </p>
            </div>
            )}

            {/* Calendar content */}
            <div className="flex-1 overflow-auto">
                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <Loader2 className="h-8 w-8 animate-spin text-[#2e7cd6]" />
                    </div>
                ) : isMobile ? (
                    <div className="p-4">
                        <MobileListView
                            visites={visites}
                            onVisiteClick={handleMobileVisiteClick}
                            searchQuery={searchQuery}
                            filterBy={filterBy}
                            isPhone={isPhone}
                            continuous={isPhone}
                        />
                    </div>
                ) : searchActive ? (
                    /* Recherche sur TOUT l'agenda (desktop) : liste des résultats toutes dates */
                    <div className="p-4 max-w-3xl">
                        <MobileListView
                            visites={visites}
                            onVisiteClick={handleMobileVisiteClick}
                            searchQuery={searchQuery}
                            filterBy={filterBy}
                            isPhone={false}
                            continuous={false}
                        />
                    </div>
                ) : (
                    <div className="p-4 space-y-4">
                        {/* Calendar grid - starts from current week */}
                        <Card className="border border-slate-200 shadow-sm">
                            <CardContent className="p-4" data-testid="calendar-container">
                                <FullCalendar
                                    ref={calendarRef}
                                    plugins={[dayGridPlugin, listPlugin, interactionPlugin]}
                                    initialView="rollingWeeks"
                                    initialDate={new Date()}
                                    views={{
                                        rollingWeeks: {
                                            type: 'dayGrid',
                                            duration: { weeks: 5 },
                                        }
                                    }}
                                    headerToolbar={false}
                                    locale="fr"
                                    firstDay={1}
                                    buttonText={{
                                        today: "Aujourd'hui",
                                        month: 'Mois',
                                        week: 'Semaine',
                                        day: 'Jour',
                                        list: 'Liste'
                                    }}
                                    events={calendarEvents}
                                    eventContent={renderEventContent}
                                    eventClick={handleEventClick}
                                    datesSet={(info) => {
                                        const start = info.start;
                                        const end = new Date(info.end);
                                        end.setDate(end.getDate() - 1);
                                        const startMonth = MOIS_NOMS[start.getMonth()];
                                        const endMonth = MOIS_NOMS[end.getMonth()];
                                        if (start.getMonth() === end.getMonth()) {
                                            setCurrentTitle(`${startMonth} ${start.getFullYear()}`);
                                        } else {
                                            setCurrentTitle(`${startMonth} - ${endMonth} ${end.getFullYear()}`);
                                        }
                                    }}
                                    height="auto"
                                    dayMaxEvents={false}
                                    eventDisplay="block"
                                    eventTimeFormat={{
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        hour12: false
                                    }}
                                    noEventsText="Aucune visite"
                                />
                            </CardContent>
                        </Card>
                    </div>
                )}
            </div>

            {/* Légende des couleurs */}
            <div className="shrink-0 px-4 py-3 bg-white border-t border-slate-200" data-testid="calendrier-legende">
                <div className="flex items-center gap-4 flex-wrap">
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Légende</span>
                    {commerciaux.map(c => (
                        <div key={c.id} className="flex items-center gap-1.5">
                            <span className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: c.couleur }} />
                            <span className="text-xs text-slate-700">{c.name}</span>
                        </div>
                    ))}
                    <div className="flex items-center gap-1.5">
                        <span className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: UNASSIGNED_VIRTUELLE_COLOR }} />
                        <span className="text-xs text-slate-600">Virtuelle non assignée</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <span className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: UNASSIGNED_PRESENTIELLE_COLOR }} />
                        <span className="text-xs text-slate-600">Présentielle non assignée</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <span className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: ANNULEE_COLOR }} />
                        <span className="text-xs text-slate-600">Annulée</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <span className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: DECALEE_COLOR }} />
                        <span className="text-xs text-slate-600">À reprogrammer</span>
                    </div>
                </div>
            </div>

            {/* Dialog */}
            <Dialog open={dialogMode !== null} onOpenChange={(open) => { if (!open) handleCloseDialog(); }}>
                <DialogContent
                    className="max-w-5xl w-full max-h-[90dvh] flex flex-col p-0 overflow-hidden"
                    onPointerDownOutside={(e) => e.preventDefault()}
                    onEscapeKeyDown={(e) => e.preventDefault()}
                    hideCloseButton
                    data-testid="visite-dialog"
                >
                    <DialogTitle className="sr-only">Visite</DialogTitle>
                    {dialogMode === 'detail' && selectedVisite && (
                        <VisiteDetailContent
                            visite={selectedVisite}
                            onClose={handleCloseDialog}
                            onEdit={() => setDialogMode('edit')}
                            onRefresh={handleDetailRefresh}
                            config={prospectConfig}
                            onReprogrammerRequest={(visite) => {
                                handleCloseDialog();
                                setTimeout(() => {
                                    setReprogrammerVisite(visite);
                                    setReprogrammerRemarque('');
                                    setShowReprogrammerModal(true);
                                }, 200);
                            }}
                            onReplanifierRequest={(visite) => {
                                // Replanification : on ouvre le formulaire d'édition
                                // complet (mêmes champs que la programmation initiale),
                                // en pré-réglant le statut à "a_venir" pour que le
                                // formulaire détecte la replanification et déclenche
                                // la confirmation email à la fin.
                                try {
                                    sessionStorage.setItem('visite_replanif_status', 'a_venir');
                                } catch { /* ignore */ }
                                setDialogMode('edit');
                            }}
                        />
                    )}
                    {dialogMode === 'edit' && selectedVisite && (
                        <VisiteFormContent
                            visite={selectedVisite}
                            prospect={{ id: selectedVisite.prospect_id, nom: selectedVisite.prospect_nom }}
                            onSuccess={handleEditSuccess}
                            onCancel={() => setDialogMode('detail')}
                            config={prospectConfig}
                        />
                    )}
                </DialogContent>
            </Dialog>

            {/* Modal Reprogrammer visite */}
            {showReprogrammerModal && reprogrammerVisite && (
                <div className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center p-4" data-testid="reprogrammer-modal">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
                        <div className="px-5 py-3 border-b border-slate-200">
                            <h2 className="text-base font-semibold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                Reprogrammer la visite — {reprogrammerVisite.prospect_nom}
                            </h2>
                        </div>
                        <div className="p-5 space-y-3">
                            <p className="text-sm text-slate-500">Une tâche sera créée automatiquement pour l'assistante.</p>
                            <div>
                                <label className="text-xs text-slate-400 block mb-1">Remarque</label>
                                <textarea
                                    value={reprogrammerRemarque}
                                    onChange={e => setReprogrammerRemarque(e.target.value)}
                                    rows={3}
                                    className="flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#2e7cd6] focus:border-transparent resize-none"
                                    placeholder="Ajouter une remarque..."
                                    autoFocus
                                    data-testid="reprogrammer-remarque"
                                />
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200">
                            <Button variant="outline" size="sm" onClick={() => { setShowReprogrammerModal(false); setReprogrammerVisite(null); }}>Annuler</Button>
                            <Button
                                size="sm"
                                className="bg-[#2e7cd6] hover:bg-[#2568b8] text-white"
                                disabled={reprogrammerSaving}
                                data-testid="reprogrammer-confirm-btn"
                                onClick={async () => {
                                    setReprogrammerSaving(true);
                                    try {
                                        await axios.put(`${API}/api/visites/${reprogrammerVisite.id}`, { statut: 'decalee' }, { withCredentials: true });
                                        await axios.post(`${API}/api/taches`, {
                                            client_nom: reprogrammerVisite.prospect_nom,
                                            titre: 'Reprogrammer la visite',
                                            personne_concernee: 'Assistante',
                                            remarque: reprogrammerRemarque,
                                            statut: 'a_traiter',
                                            linked_visite_id: reprogrammerVisite.id,
                                            type_rappel: 'visite_reprogrammer',
                                        }, { withCredentials: true });
                                        toast.success('Visite à reprogrammer — tâche créée');
                                        setShowReprogrammerModal(false);
                                        setReprogrammerVisite(null);
                                        fetchVisites();
                                    } catch (error) {
                                        toast.error(error.response?.data?.detail || 'Erreur');
                                    } finally { setReprogrammerSaving(false); }
                                }}
                            >
                                {reprogrammerSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
                                Confirmer
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
